import { createPortal } from 'react-dom';
import { Check, Users } from 'lucide-react';
import { useUIStore } from '@/stores/uiStore';
import type { ProjectGroup } from '@/services/backend/IBackend';

/**
 * A small floating menu to move a project into a team (or Ungrouped), shown at a
 * screen point (e.g. a right-click). Portaled to <body> so a panel's overflow
 * doesn't clip it. Shared by the Projects panel and the Home dashboard.
 */
export default function TeamContextMenu({
  x, y, currentTeamId, groups, onPick, onClose,
}: {
  x: number;
  y: number;
  currentTeamId: string | null;
  groups: ProjectGroup[];
  onPick: (teamId: string | null) => void;
  onClose: () => void;
}) {
  return createPortal(
    <div
      className="fixed inset-0 z-[10000]"
      onClick={onClose}
      onContextMenu={(e) => { e.preventDefault(); onClose(); }}
    >
      <div
        className="absolute bg-base-100 border border-base-300 rounded-lg shadow-2xl py-1 min-w-[12rem] max-h-[60vh] overflow-y-auto"
        style={{ left: Math.min(x, window.innerWidth - 220), top: Math.min(y, window.innerHeight - 320) }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-base-content/40">Move to team</div>
        {groups.map((g) => (
          <button key={g.id} onClick={() => onPick(g.id)} className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-base-200 text-left">
            <span className="w-2 h-2 rounded-full shrink-0 border border-base-content/20" style={{ backgroundColor: g.color ?? 'transparent' }} />
            <span className="flex-1 truncate">{g.name}</span>
            {currentTeamId === g.id && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
          </button>
        ))}
        <button onClick={() => onPick(null)} className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-base-200 text-left">
          <span className="w-2 h-2 rounded-full shrink-0 border border-base-content/20" />
          <span className="flex-1 truncate text-base-content/70">Ungrouped</span>
          {currentTeamId === null && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
        </button>
        <div className="border-t border-base-300 mt-1 pt-1">
          <button
            onClick={() => { onClose(); useUIStore.getState().openSettingsTo('app-groups'); }}
            className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-base-200 text-left text-base-content/70"
          >
            <Users className="w-3.5 h-3.5 shrink-0" />
            <span>Manage teams…</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
